import type {
  PublicMarketplaceCapabilitiesResponse,
  RealEstateEntityType,
  TransactionType,
  UnitType,
} from './types';
import { normalizeForComparison } from './stringUtils';

const SLUG_SEPARATOR = '-';
const LISTING_ID_SUFFIX_LENGTH = 8;
const PUBLIC_LISTING_SUFFIX_PREFIX = '-ir-';
export const PUBLIC_LISTING_SLUG_MAX_LENGTH = 255;
export const MARKETPLACE_IDEMPOTENCY_KEY_HEADER_NAME =
  'X-Idempotency-Key' as const;
export const PUBLIC_MARKETPLACE_ANALYTICS_SESSION_HEADER_NAME =
  'X-Inmueble-Analytics-Session-Hash' as const;
export const PUBLIC_MARKETPLACE_PUBLIC_API_PREFIX = '/api/public/' as const;
export const PUBLIC_MARKETPLACE_AUTH_API_PREFIX = '/api/auth/' as const;
export const PUBLIC_MARKETPLACE_BUYER_MARKETPLACE_INQUIRY_API_PREFIX =
  '/api/buyer/marketplace/listings/' as const;
export const PUBLIC_MARKETPLACE_BUYER_SAVED_LISTINGS_API_PREFIX =
  '/api/buyer/saved-listings' as const;
export const PUBLIC_MARKETPLACE_BUYER_SAVED_SEARCHES_API_PREFIX =
  '/api/buyer/saved-searches' as const;
export const PUBLIC_MARKETPLACE_BUYER_ACCOUNT_API_PREFIX =
  '/api/buyer/account' as const;
export const PUBLIC_MARKETPLACE_BUYER_EMAIL_PREFERENCES_API_PATH =
  '/api/buyer/email-preferences' as const;
export const PUBLIC_MARKETPLACE_FAIL_CLOSED_CAPABILITIES = Object.freeze({
  soloPublisherSignup: Object.freeze({
    agent: Object.freeze({
      enabled: false,
      disabledReason: 'solo_publisher_signup_disabled',
    }),
    individual: Object.freeze({
      enabled: false,
      disabledReason: 'solo_publisher_signup_disabled',
    }),
  }),
}) satisfies PublicMarketplaceCapabilitiesResponse;
export const PUBLIC_MARKETPLACE_SITEMAP_PREFIX = '/sitemaps/' as const;
export const PUBLIC_MARKETPLACE_ROBOTS_PATH = '/robots.txt' as const;
export const PUBLIC_MARKETPLACE_LLMS_PATH = '/llms.txt' as const;
export const PUBLIC_MARKETPLACE_BROWSER_ASSET_PATH_PREFIX = '/assets/' as const;
export const PUBLIC_MARKETPLACE_COUNTRY_PATH_SLUG = 'peru' as const;
export type PublicMarketplaceCountryCode = 'PE';
export interface PublicMarketplaceCountry {
  code: PublicMarketplaceCountryCode;
  flag: string;
  label: string;
  locationPathSlug: typeof PUBLIC_MARKETPLACE_COUNTRY_PATH_SLUG;
}
export const PUBLIC_MARKETPLACE_COUNTRIES = [
  {
    code: 'PE',
    flag: '🇵🇪',
    label: 'Perú',
    locationPathSlug: PUBLIC_MARKETPLACE_COUNTRY_PATH_SLUG,
  },
] as const satisfies readonly PublicMarketplaceCountry[];
export const PUBLIC_MARKETPLACE_AGENCIES_PATH = '/inmobiliarias' as const;
export const PUBLIC_MARKETPLACE_ROOT_ASSET_PATHS = [
  '/apple-touch-icon.png',
  '/favicon.ico',
  '/favicon.svg',
  '/favicon-16.png',
  '/favicon-32.png',
  '/logo.svg',
  '/marketplace-hero.svg',
  '/marketplace-social-card.png',
  '/vivienda-social-family.jpg',
  '/vivienda-social-family-480.webp',
  '/vivienda-social-family-960.webp',
  '/vivienda-social-family-1536.webp',
] as const;
export const PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES = {
  login: '/login',
  register: '/registro',
  verifyEmail: '/verify-email',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  forgotPasswordLocalized: '/recuperar-contrasena',
  resetPasswordLocalized: '/restablecer-contrasena',
  account: '/cuenta',
  buyerAgent: '/cuenta/mi-agente',
  interests: '/cuenta/intereses',
  profile: '/cuenta/datos-personales',
  loginSecurity: '/cuenta/inicio-sesion',
  emailPreferences: '/cuenta/email',
  favorites: '/favoritos',
  savedSearches: '/busquedas-guardadas',
  guidedSearch: '/busqueda-guiada',
  searchBriefs: '/cuenta/objetivos-de-busqueda',
  visits: '/cuenta/visitas',
  plan: '/cuenta/plan-de-compra',
  properties: '/cuenta/mis-propiedades',
  rentals: '/cuenta/alquileres',
} as const;
export const PUBLIC_MARKETPLACE_SAVED_SEARCH_OPEN_PATH_PREFIX =
  '/busquedas-guardadas/' as const;

// Only account surfaces that do not query the general marketplace are available here.
export function isViviendaSocialAccountPath(pathname: string): boolean {
  const path = pathname.replace(/\/+$/, '');
  return [
    '/login',
    '/registro',
    '/verify-email',
    '/forgot-password',
    '/reset-password',
    '/recuperar-contrasena',
    '/restablecer-contrasena',
    '/cuenta',
    '/cuenta/datos-personales',
    '/cuenta/inicio-sesion',
    '/cuenta/email',
    '/favoritos',
  ].includes(path);
}

export function isViviendaSocialRestrictedApiPath(pathname: string): boolean {
  if (pathname.startsWith('/api/buyer/')) {
    if (
      /^\/api\/buyer\/marketplace\/(?:listings\/[a-z0-9-]+\/inquiries|inquiries\/[a-z0-9-]+\/claim)$/.test(
        pathname
      )
    )
      return false;
    return !/^\/api\/buyer\/(account(?:\/profile)?|email-preferences|saved-listings(?:\/status|\/[a-z0-9-]+(?:\/alerts)?)?)\/?$/.test(
      pathname
    );
  }
  return /^\/api\/public\/marketplace\/(match-sessions|buyer-agent)(?:\/|$)/.test(
    pathname
  );
}
export const PUBLIC_MARKETPLACE_IMPORTED_CLAIM_PATH_PREFIX =
  '/reclamar-publicacion/' as const;
export const PUBLIC_MARKETPLACE_SIGNED_OPPOSITION_PATH_PREFIX =
  '/oposicion/' as const;
export const PUBLIC_MARKETPLACE_ACCOUNT_APP_PATHS = [
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.login,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.register,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.verifyEmail,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.forgotPassword,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.resetPassword,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.forgotPasswordLocalized,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.resetPasswordLocalized,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.account,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.buyerAgent,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.interests,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.profile,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.loginSecurity,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.emailPreferences,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.favorites,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.savedSearches,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.guidedSearch,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.searchBriefs,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.visits,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.plan,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.properties,
  PUBLIC_MARKETPLACE_ACCOUNT_APP_ROUTES.rentals,
] as const;
export type PublicMarketplaceAccountAppPath =
  | (typeof PUBLIC_MARKETPLACE_ACCOUNT_APP_PATHS)[number]
  | `${typeof PUBLIC_MARKETPLACE_SAVED_SEARCH_OPEN_PATH_PREFIX}${string}`
  | `${typeof PUBLIC_MARKETPLACE_IMPORTED_CLAIM_PATH_PREFIX}${string}`
  | `${typeof PUBLIC_MARKETPLACE_SIGNED_OPPOSITION_PATH_PREFIX}${string}`;
export const PUBLIC_MARKETPLACE_PRODUCT_PAGE_PATHS = [
  '/para-empresas',
  '/para-empresas/',
  '/desarrolladoras',
  '/desarrolladoras/',
  '/soluciones/inmobiliarias',
  '/soluciones/inmobiliarias/',
  '/webs-para-proyectos',
  '/webs-para-proyectos/',
  '/assist',
  '/assist/',
  '/precios',
  '/precios/',
  '/hipotecas',
  '/hipotecas/',
  '/publicar-inmuebles-gratis',
  '/publicar-inmuebles-gratis/',
  '/publicar-como-propietario',
  '/publicar-como-propietario/',
  '/publicar-como-agente',
  '/publicar-como-agente/',
  '/publicar-como-inmobiliaria',
  '/publicar-como-inmobiliaria/',
  '/publicar-proyectos',
  '/publicar-proyectos/',
] as const;
export const PUBLIC_MARKETPLACE_STATIC_PAGE_PATHS = [
  '/preguntas-frecuentes',
  '/preguntas-frecuentes/',
  '/contacto',
  '/contacto/',
  '/seguridad',
  '/seguridad/',
  '/nosotros',
  '/nosotros/',
] as const;
export const PUBLIC_MARKETPLACE_LEGAL_PAGE_PATHS = [
  '/legal/libro-reclamos',
  '/legal/libro-reclamos/',
  '/libro-de-reclamaciones',
  '/libro-de-reclamaciones/',
  '/legal/privacy',
  '/legal/privacy/',
  '/privacy',
  '/privacy/',
  '/legal/terms-website',
  '/legal/terms-website/',
  '/terms',
  '/terms/',
  '/legal/cookies',
  '/legal/cookies/',
] as const;
export const PUBLIC_MARKETPLACE_CANONICAL_STATIC_PAGE_PATHS = [
  '/preguntas-frecuentes/',
  '/contacto/',
  '/seguridad/',
  '/nosotros/',
] as const;
export const PUBLIC_MARKETPLACE_CANONICAL_LEGAL_PAGE_PATHS = [
  '/legal/libro-reclamos',
  '/legal/privacy',
  '/legal/terms-website',
  '/legal/cookies',
] as const;
export type PublicMarketplaceStaticPagePath =
  (typeof PUBLIC_MARKETPLACE_STATIC_PAGE_PATHS)[number];
export type PublicMarketplaceLegalPagePath =
  (typeof PUBLIC_MARKETPLACE_LEGAL_PAGE_PATHS)[number];
export type PublicMarketplaceCanonicalStaticPagePath =
  (typeof PUBLIC_MARKETPLACE_CANONICAL_STATIC_PAGE_PATHS)[number];
export type PublicMarketplaceCanonicalLegalPagePath =
  (typeof PUBLIC_MARKETPLACE_CANONICAL_LEGAL_PAGE_PATHS)[number];
const PUBLIC_LISTING_SLUG_SUFFIX_LENGTH =
  PUBLIC_LISTING_SUFFIX_PREFIX.length + LISTING_ID_SUFFIX_LENGTH;
const PUBLIC_LISTING_SEMANTIC_MAX_LENGTH =
  PUBLIC_LISTING_SLUG_MAX_LENGTH - PUBLIC_LISTING_SLUG_SUFFIX_LENGTH;
const PUBLIC_MARKETPLACE_AGENCY_LISTINGS_SEGMENT_COUNT = 3;
const PUBLIC_MARKETPLACE_AGENCY_LISTINGS_SEGMENT_INDEX = 2;
const PUBLIC_MARKETPLACE_ROOT_ASSET_PATH_SET = new Set<string>(
  PUBLIC_MARKETPLACE_ROOT_ASSET_PATHS
);
const PUBLIC_MARKETPLACE_ACCOUNT_APP_PATH_SET = new Set<string>(
  PUBLIC_MARKETPLACE_ACCOUNT_APP_PATHS
);
const PUBLIC_MARKETPLACE_PRODUCT_PAGE_PATH_SET = new Set<string>(
  PUBLIC_MARKETPLACE_PRODUCT_PAGE_PATHS
);
const PUBLIC_MARKETPLACE_STATIC_PAGE_PATH_SET = new Set<string>(
  PUBLIC_MARKETPLACE_STATIC_PAGE_PATHS
);
const PUBLIC_MARKETPLACE_LEGAL_PAGE_PATH_SET = new Set<string>(
  PUBLIC_MARKETPLACE_LEGAL_PAGE_PATHS
);
const PUBLIC_MARKETPLACE_STATIC_CANONICAL_BY_PATH = new Map<
  string,
  PublicMarketplaceCanonicalStaticPagePath
>([
  ['/preguntas-frecuentes', '/preguntas-frecuentes/'],
  ['/contacto', '/contacto/'],
  ['/seguridad', '/seguridad/'],
  ['/nosotros', '/nosotros/'],
]);
const PUBLIC_MARKETPLACE_LEGAL_CANONICAL_BY_PATH = new Map<
  string,
  PublicMarketplaceCanonicalLegalPagePath
>([
  ['/legal/libro-reclamos', '/legal/libro-reclamos'],
  ['/libro-de-reclamaciones', '/legal/libro-reclamos'],
  ['/legal/privacy', '/legal/privacy'],
  ['/privacy', '/legal/privacy'],
  ['/legal/terms-website', '/legal/terms-website'],
  ['/terms', '/legal/terms-website'],
  ['/legal/cookies', '/legal/cookies'],
]);
export const PUBLIC_LISTING_SUFFIX_PATTERN = /-ir-[a-z0-9]{5,}$/;

export function normalizePublicLocationQuery(value: string): string {
  return normalizeForComparison(value)
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

const UNIT_TYPE_PUBLIC_LABELS: Partial<Record<UnitType, string>> = {
  apartment: 'departamento',
  house: 'casa',
  office: 'oficina',
  retail_local: 'local-comercial',
  lot: 'lote',
  industrial_lot: 'lote-industrial',
  agricultural_parcel: 'parcela-agricola',
  warehouse_unit: 'almacen',
  garage: 'estacionamiento',
  garage_with_storage: 'estacionamiento',
  studio: 'studio',
  duplex: 'duplex',
  triplex: 'triplex',
  penthouse: 'penthouse',
  townhouse_unit: 'townhouse',
};

const TRANSACTION_PUBLIC_LABELS: Partial<Record<TransactionType, string>> = {
  sale: 'en-venta',
  rent: 'en-alquiler',
};

export interface PublicMarketplaceFacet {
  entityType?: RealEstateEntityType;
  unitType?: UnitType;
  transactionType: TransactionType;
  viviendaSocial?: boolean;
  slug: string;
  label: string;
}

export type PublicMarketplaceRoute =
  | {
      kind: 'catalog';
      locationPathSlug: string;
      facet: PublicMarketplaceFacet | null;
    }
  | {
      kind: 'listing';
      locationPathSlug: string;
      publicSlug: string;
    }
  | {
      kind: 'agency-directory';
    }
  | {
      kind: 'agency-profile';
      agencySlug: string;
      listingsOnly: boolean;
    }
  | {
      kind: 'market-landing';
    }
  | {
      kind: 'market';
      locationPathSlug: string;
    }
  | {
      kind: 'professional';
      slug: string;
    }
  | {
      kind: 'product';
      path: (typeof PUBLIC_MARKETPLACE_PRODUCT_PAGE_PATHS)[number];
    }
  | {
      kind: 'static';
      path: PublicMarketplaceStaticPagePath;
      canonicalPath: PublicMarketplaceCanonicalStaticPagePath;
    }
  | {
      kind: 'legal';
      path: PublicMarketplaceLegalPagePath;
      canonicalPath: PublicMarketplaceCanonicalLegalPagePath;
    }
  | {
      kind: 'app';
      path: PublicMarketplaceAccountAppPath;
    };

export const PUBLIC_MARKETPLACE_INDEXABLE_FACETS: readonly PublicMarketplaceFacet[] =
  [
    {
      transactionType: 'sale',
      slug: 'inmuebles-en-venta',
      label: 'Inmuebles en venta',
    },
    {
      transactionType: 'rent',
      slug: 'inmuebles-en-alquiler',
      label: 'Inmuebles en alquiler',
    },
    {
      transactionType: 'sale',
      viviendaSocial: true,
      slug: 'vivienda-social',
      label: 'Vivienda social',
    },
    {
      unitType: 'apartment',
      transactionType: 'sale',
      slug: 'departamentos-en-venta',
      label: 'Departamentos en venta',
    },
    {
      unitType: 'apartment',
      transactionType: 'rent',
      slug: 'departamentos-en-alquiler',
      label: 'Departamentos en alquiler',
    },
    {
      unitType: 'house',
      transactionType: 'sale',
      slug: 'casas-en-venta',
      label: 'Casas en venta',
    },
    {
      unitType: 'house',
      transactionType: 'rent',
      slug: 'casas-en-alquiler',
      label: 'Casas en alquiler',
    },
    {
      unitType: 'office',
      transactionType: 'rent',
      slug: 'oficinas-en-alquiler',
      label: 'Oficinas en alquiler',
    },
    {
      unitType: 'office',
      transactionType: 'sale',
      slug: 'oficinas-en-venta',
      label: 'Oficinas en venta',
    },
    {
      unitType: 'lot',
      transactionType: 'sale',
      slug: 'lotes-en-venta',
      label: 'Lotes en venta',
    },
    {
      unitType: 'lot',
      transactionType: 'rent',
      slug: 'lotes-en-alquiler',
      label: 'Lotes en alquiler',
    },
    {
      unitType: 'retail_local',
      transactionType: 'sale',
      slug: 'locales-comerciales-en-venta',
      label: 'Locales comerciales en venta',
    },
    {
      unitType: 'retail_local',
      transactionType: 'rent',
      slug: 'locales-comerciales-en-alquiler',
      label: 'Locales comerciales en alquiler',
    },
    {
      unitType: 'warehouse_unit',
      transactionType: 'sale',
      slug: 'almacenes-en-venta',
      label: 'Almacenes en venta',
    },
    {
      unitType: 'warehouse_unit',
      transactionType: 'rent',
      slug: 'almacenes-en-alquiler',
      label: 'Almacenes en alquiler',
    },
    {
      entityType: 'project',
      transactionType: 'sale',
      slug: 'proyectos-en-venta',
      label: 'Proyectos en venta',
    },
    {
      entityType: 'project',
      transactionType: 'rent',
      slug: 'proyectos-en-alquiler',
      label: 'Proyectos en alquiler',
    },
  ] as const;

const PUBLIC_MARKETPLACE_FACET_BY_SLUG = new Map(
  PUBLIC_MARKETPLACE_INDEXABLE_FACETS.map((facet) => [facet.slug, facet])
);

export function normalizePublicPathSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/^\/+/, '')
    .replace(/\/+$/, '')
    .replace(/\/{2,}/g, '/');
}

export function buildCanonicalPublicPath(pathSlug: string): string {
  const normalized = normalizePublicPathSlug(pathSlug);
  return normalized ? `/${normalized}/` : '/';
}

export function buildPublicMarketplaceCatalogPath(
  locationPathSlug: string,
  facet: PublicMarketplaceFacet | null
): string {
  const locationPath = buildCanonicalPublicPath(locationPathSlug);
  if (facet?.viviendaSocial === true) {
    return `/${facet.slug}${locationPath}`;
  }
  if (
    facet === null &&
    normalizePublicPathSlug(locationPathSlug) ===
      PUBLIC_MARKETPLACE_COUNTRY_PATH_SLUG
  ) {
    return '/';
  }
  return facet ? `${locationPath}${facet.slug}/` : locationPath;
}

export function resolvePublicMarketplaceCountryFromPathSlug(
  locationPathSlug: string
): PublicMarketplaceCountry {
  const normalizedLocationPathSlug = normalizePublicPathSlug(locationPathSlug);
  const country =
    PUBLIC_MARKETPLACE_COUNTRIES.find(
      (candidate) =>
        normalizedLocationPathSlug === candidate.locationPathSlug ||
        normalizedLocationPathSlug.startsWith(`${candidate.locationPathSlug}/`)
    ) ?? null;

  if (!country) {
    throw new Error(
      `Unsupported public marketplace country path slug: ${locationPathSlug}`
    );
  }

  return country;
}

export function buildPublicMarketplaceAgencyPath(input?: {
  agencySlug?: string;
  listingsOnly?: boolean;
}): string {
  const agencySlug = input?.agencySlug?.trim().toLowerCase();
  if (!agencySlug) {
    return `${PUBLIC_MARKETPLACE_AGENCIES_PATH}/`;
  }
  return `${PUBLIC_MARKETPLACE_AGENCIES_PATH}/${agencySlug}${input?.listingsOnly ? '/inmuebles' : ''}/`;
}

export function buildPublicMarketplaceSavedSearchPath(
  savedSearchId: string
): string {
  const normalizedSavedSearchId = savedSearchId.trim();
  if (!normalizedSavedSearchId) {
    throw new Error('Saved search ID is required');
  }
  return `${PUBLIC_MARKETPLACE_SAVED_SEARCH_OPEN_PATH_PREFIX}${encodeURIComponent(normalizedSavedSearchId)}`;
}

export function isPublicMarketplaceSavedSearchOpenPath(
  pathname: string
): boolean {
  const normalizedPath = normalizePublicPathSlug(pathname);
  const normalizedPathname = normalizedPath ? `/${normalizedPath}` : '/';
  if (
    !normalizedPathname.startsWith(
      PUBLIC_MARKETPLACE_SAVED_SEARCH_OPEN_PATH_PREFIX
    )
  ) {
    return false;
  }

  const savedSearchId = normalizedPathname.slice(
    PUBLIC_MARKETPLACE_SAVED_SEARCH_OPEN_PATH_PREFIX.length
  );
  return savedSearchId.length > 0 && !savedSearchId.includes('/');
}

function isSingleSegmentPrefixedPath(
  normalizedPathname: string,
  prefix: string
): boolean {
  if (!normalizedPathname.startsWith(prefix)) return false;
  const suffix = normalizedPathname.slice(prefix.length);
  return suffix.length > 0 && !suffix.includes('/');
}

export function parsePublicMarketplaceRoute(
  pathname: string
): PublicMarketplaceRoute {
  const normalizedPath = normalizePublicPathSlug(pathname);
  const normalizedPathname = normalizedPath ? `/${normalizedPath}` : '/';

  if (
    isSingleSegmentPrefixedPath(
      normalizedPathname,
      PUBLIC_MARKETPLACE_IMPORTED_CLAIM_PATH_PREFIX
    ) ||
    isSingleSegmentPrefixedPath(
      normalizedPathname,
      PUBLIC_MARKETPLACE_SIGNED_OPPOSITION_PATH_PREFIX
    )
  ) {
    return {
      kind: 'app',
      path: normalizedPathname as PublicMarketplaceAccountAppPath,
    };
  }

  if (
    PUBLIC_MARKETPLACE_ACCOUNT_APP_PATH_SET.has(normalizedPathname) ||
    isPublicMarketplaceSavedSearchOpenPath(normalizedPathname)
  ) {
    return {
      kind: 'app',
      path: normalizedPathname as PublicMarketplaceAccountAppPath,
    };
  }

  if (PUBLIC_MARKETPLACE_PRODUCT_PAGE_PATH_SET.has(normalizedPathname)) {
    return {
      kind: 'product',
      path: normalizedPathname as (typeof PUBLIC_MARKETPLACE_PRODUCT_PAGE_PATHS)[number],
    };
  }

  const staticCanonicalPath =
    PUBLIC_MARKETPLACE_STATIC_CANONICAL_BY_PATH.get(normalizedPathname);
  if (staticCanonicalPath) {
    return {
      kind: 'static',
      path: normalizedPathname as PublicMarketplaceStaticPagePath,
      canonicalPath: staticCanonicalPath,
    };
  }

  const legalCanonicalPath =
    PUBLIC_MARKETPLACE_LEGAL_CANONICAL_BY_PATH.get(normalizedPathname);
  if (legalCanonicalPath) {
    return {
      kind: 'legal',
      path: normalizedPathname as PublicMarketplaceLegalPagePath,
      canonicalPath: legalCanonicalPath,
    };
  }

  const segments = normalizedPath.split('/').filter(Boolean);
  if (segments.length === 0) {
    return {
      kind: 'catalog',
      locationPathSlug: PUBLIC_MARKETPLACE_COUNTRY_PATH_SLUG,
      facet: null,
    };
  }

  if (segments[0] === 'inmobiliarias') {
    if (segments.length === 1) {
      return { kind: 'agency-directory' };
    }

    if (segments.length === 2) {
      return {
        kind: 'agency-profile',
        agencySlug: segments[1] ?? '',
        listingsOnly: false,
      };
    }

    if (
      segments.length === PUBLIC_MARKETPLACE_AGENCY_LISTINGS_SEGMENT_COUNT &&
      segments[PUBLIC_MARKETPLACE_AGENCY_LISTINGS_SEGMENT_INDEX] === 'inmuebles'
    ) {
      return {
        kind: 'agency-profile',
        agencySlug: segments[1] ?? '',
        listingsOnly: true,
      };
    }
  }

  if (segments[0] === 'mercado') {
    if (segments.length === 1) {
      return { kind: 'market-landing' };
    }
    return {
      kind: 'market',
      locationPathSlug: segments.slice(1).join('/'),
    };
  }

  if (segments[0] === 'profesionales' && segments.length === 2) {
    return {
      kind: 'professional',
      slug: segments[1] ?? '',
    };
  }

  const viviendaSocialFacet = resolvePublicMarketplaceFacet('vivienda-social');
  if (
    (segments[0] === 'vivienda-social' || segments[0] === 'techo-propio') &&
    viviendaSocialFacet
  ) {
    return {
      kind: 'catalog',
      locationPathSlug:
        segments.length > 1
          ? segments.slice(1).join('/')
          : PUBLIC_MARKETPLACE_COUNTRY_PATH_SLUG,
      facet: viviendaSocialFacet,
    };
  }

  const finalSegment = segments[segments.length - 1] ?? '';
  const facet = resolvePublicMarketplaceFacet(finalSegment);
  if (facet && segments.length > 1) {
    return {
      kind: 'catalog',
      locationPathSlug: segments.slice(0, -1).join('/'),
      facet,
    };
  }

  if (segments.length > 1 && PUBLIC_LISTING_SUFFIX_PATTERN.test(finalSegment)) {
    return {
      kind: 'listing',
      locationPathSlug: segments.slice(0, -1).join('/'),
      publicSlug: finalSegment,
    };
  }

  return {
    kind: 'catalog',
    locationPathSlug: normalizedPath || PUBLIC_MARKETPLACE_COUNTRY_PATH_SLUG,
    facet: null,
  };
}

export function isPublicMarketplaceAllowedPublicPath(
  pathname: string
): boolean {
  const normalizedPath = normalizePublicPathSlug(pathname);
  const normalizedPathname = normalizedPath ? `/${normalizedPath}` : '/';
  const parsedRoute = parsePublicMarketplaceRoute(pathname);

  return (
    pathname === '/' ||
    PUBLIC_MARKETPLACE_ROOT_ASSET_PATH_SET.has(pathname) ||
    pathname.startsWith(PUBLIC_MARKETPLACE_BROWSER_ASSET_PATH_PREFIX) ||
    PUBLIC_MARKETPLACE_ACCOUNT_APP_PATH_SET.has(normalizedPathname) ||
    isPublicMarketplaceSavedSearchOpenPath(normalizedPathname) ||
    isSingleSegmentPrefixedPath(
      normalizedPathname,
      PUBLIC_MARKETPLACE_IMPORTED_CLAIM_PATH_PREFIX
    ) ||
    isSingleSegmentPrefixedPath(
      normalizedPathname,
      PUBLIC_MARKETPLACE_SIGNED_OPPOSITION_PATH_PREFIX
    ) ||
    PUBLIC_MARKETPLACE_PRODUCT_PAGE_PATH_SET.has(pathname) ||
    PUBLIC_MARKETPLACE_STATIC_PAGE_PATH_SET.has(pathname) ||
    PUBLIC_MARKETPLACE_STATIC_CANONICAL_BY_PATH.has(normalizedPathname) ||
    PUBLIC_MARKETPLACE_LEGAL_PAGE_PATH_SET.has(pathname) ||
    PUBLIC_MARKETPLACE_LEGAL_CANONICAL_BY_PATH.has(normalizedPathname) ||
    pathname === '/robots.txt' ||
    pathname === '/sitemap.xml' ||
    pathname === PUBLIC_MARKETPLACE_ROBOTS_PATH ||
    pathname === PUBLIC_MARKETPLACE_LLMS_PATH ||
    pathname === '/vivienda-social' ||
    pathname.startsWith('/vivienda-social/') ||
    pathname === '/techo-propio' ||
    pathname.startsWith('/techo-propio/') ||
    pathname === '/peru' ||
    pathname.startsWith('/peru/') ||
    pathname === '/inmobiliarias' ||
    pathname.startsWith('/inmobiliarias/') ||
    pathname === '/mercado' ||
    pathname.startsWith('/mercado/') ||
    pathname.startsWith('/profesionales/') ||
    parsedRoute.kind === 'listing' ||
    pathname.startsWith(PUBLIC_MARKETPLACE_SITEMAP_PREFIX)
  );
}

export function resolvePublicMarketplaceFacet(
  slug: string
): PublicMarketplaceFacet | null {
  return (
    PUBLIC_MARKETPLACE_FACET_BY_SLUG.get(slug.trim().toLowerCase()) ?? null
  );
}

export function slugifyPublicMarketplaceText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, SLUG_SEPARATOR)
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, SLUG_SEPARATOR);
}

function truncatePublicListingSemanticSlug(value: string): string {
  const truncatedSlug = value
    .slice(0, PUBLIC_LISTING_SEMANTIC_MAX_LENGTH)
    .replace(/-+$/g, '');
  return truncatedSlug || 'inmueble';
}

export function buildPublicListingSlug(input: {
  id: string;
  entityType: RealEstateEntityType;
  unitType?: UnitType | null;
  transactionType: TransactionType;
  title: string;
  address?: string | null;
  existingSlug?: string | null;
}): string {
  const unitLabel =
    input.entityType === 'project'
      ? 'proyecto'
      : input.unitType
        ? (UNIT_TYPE_PUBLIC_LABELS[input.unitType] ?? 'inmueble')
        : 'inmueble';
  const transactionLabel =
    TRANSACTION_PUBLIC_LABELS[input.transactionType] ?? 'disponible';
  const titleOrAddress =
    input.address?.trim() ||
    input.title.trim() ||
    input.existingSlug?.trim() ||
    input.id;
  const semanticSlug = slugifyPublicMarketplaceText(
    `${unitLabel} ${transactionLabel} ${titleOrAddress}`
  );
  const stableSuffix = input.id
    .replace(/-/g, '')
    .slice(-LISTING_ID_SUFFIX_LENGTH);

  return `${truncatePublicListingSemanticSlug(semanticSlug)}${PUBLIC_LISTING_SUFFIX_PREFIX}${stableSuffix}`;
}
